/**
 * VideoErrorBoundary - Error boundary for video room
 * Catches React errors and shows recovery UI
 */

import * as React from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface VideoErrorBoundaryProps {
  children: React.ReactNode;
  onReset?: () => void;
  onGoHome?: () => void;
  className?: string;
}

interface VideoErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class VideoErrorBoundary extends React.Component<
  VideoErrorBoundaryProps,
  VideoErrorBoundaryState
> {
  constructor(props: VideoErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): VideoErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Video room error:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className={cn(
            "min-h-screen bg-stone-950 flex items-center justify-center p-4",
            this.props.className
          )}
        >
          <div className="bg-card rounded-2xl p-8 max-w-md text-center shadow-xl">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>

            <h2 className="text-xl font-semibold mb-2">
              Ein Fehler ist aufgetreten
            </h2>

            <p className="text-muted-foreground mb-6">
              Bei der Video-Verbindung ist ein unerwarteter Fehler aufgetreten.
              Bitte versuchen Sie es erneut.
            </p>

            {this.state.error && (
              <details className="text-left mb-6">
                <summary className="text-xs text-muted-foreground cursor-pointer">
                  Technische Details
                </summary>
                <pre className="text-xs bg-muted p-3 rounded-lg mt-2 overflow-auto max-h-32">
                  {this.state.error.message}
                </pre>
              </details>
            )}

            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleReset}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Erneut versuchen
              </Button>

              {this.props.onGoHome && (
                <Button variant="outline" onClick={this.props.onGoHome}>
                  <Home className="w-4 h-4 mr-2" />
                  Zur Startseite
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
